import {
  Document,
  Packer,
  Paragraph,
  TextRun,
  Footer,
  Header,
  PageNumber,
  Table,
  TableRow,
  TableCell,
  ImageRun,
  AlignmentType,
  BorderStyle,
  HeadingLevel,
  LevelFormat,
  convertInchesToTwip,
  UnderlineType,
} from 'docx';
import { saveAs } from 'file-saver';

import ConfideHeader from '../../../assets/png/ConfideDocHeader.png';
import { fieldTypes } from '../../../components/customer/settings/finalReport/templateDialogs/common/fieldTypes';
import { getCurrentIntl } from '../../../i18n/intl/intl';
import { FinalReportDocumentField } from '../../../interfaces/FinalReportDocumentField';
import { LooseObject } from '../../../interfaces/LooseObject';
import { ModifiedFinalReportDocument } from '../../../interfaces/ModifiedFinalReportDocument';

type DocxObject = Paragraph | Table;

interface Bullet {
  reference: string;
  level: number;
}

interface SpanStyle {
  size: string | null;
}

const DEFAULT_FONT = 'Calibri';
const ONE_FIVE_LINE_SPACING = 360;

const fontColors = {
  black: '181818',
  gray: '878787',
  darkGray: '4F4F4F',
};

const footerBorder = {
  top: {
    style: BorderStyle.SINGLE,
    size: 1,
    color: fontColors.gray,
  },
  left: {
    style: BorderStyle.NIL,
    size: 0,
  },
  right: {
    style: BorderStyle.NIL,
    size: 0,
  },
  bottom: {
    style: BorderStyle.NIL,
    size: 0,
  },
};

const noBorder = {
  top: {
    style: BorderStyle.NIL,
    size: 0,
  },
  left: {
    style: BorderStyle.NIL,
    size: 0,
  },
  right: {
    style: BorderStyle.NIL,
    size: 0,
  },
  bottom: {
    style: BorderStyle.NIL,
    size: 0,
  },
};

const bottomBorder = {
  top: {
    style: BorderStyle.NIL,
    size: 0,
  },
  left: {
    style: BorderStyle.NIL,
    size: 0,
  },
  right: {
    style: BorderStyle.NIL,
    size: 0,
  },
  bottom: {
    style: BorderStyle.SINGLE,
    size: 1,
    color: fontColors.gray,
  },
};

const createParagraph = (
  content: TextRun[],
  bulletLevel: Bullet | null,
  headingLevel: string,
) => {
  const paragraphProps: LooseObject = {
    children: content,
    indent: {
      left: convertInchesToTwip(0.3),
    },
    spacing: {
      line: ONE_FIVE_LINE_SPACING,
      lineRule: 'auto',
    },
  };
  if (bulletLevel) {
    paragraphProps.numbering = bulletLevel;
  }
  if (headingLevel !== '') {
    paragraphProps.heading = headingLevel;
  }
  return new Paragraph(paragraphProps);
};

const createSmallFieldTitleCell = (text: string) => {
  return new TableCell({
    borders: noBorder,
    children: [
      new Paragraph({
        children: [
          new TextRun({
            children: [text],
            bold: true,
            font: DEFAULT_FONT,
            size: '12pt',
          }),
        ],
      }),
    ],
  });
};

const createSmallFieldValueCell = (text: string) => {
  return new TableCell({
    borders: noBorder,
    children: [
      new Paragraph({
        children: [
          new TextRun({
            children: [text],
            font: DEFAULT_FONT,
            size: '12pt',
          }),
        ],
      }),
    ],
  });
};

const createSmallFieldTable = (field: FinalReportDocumentField) => {
  const titleCells: TableCell[] = [createSmallFieldTitleCell(field.title)];
  const valueCells: TableCell[] = [createSmallFieldValueCell(field.value)];

  if (field.siblings) {
    const siblingTitleCells = field.siblings.map((sibling) => {
      return createSmallFieldTitleCell(sibling.title);
    });

    const siblingValueCells = field.siblings.map((sibling) => {
      return createSmallFieldValueCell(sibling.value);
    });

    titleCells.push(...siblingTitleCells);
    valueCells.push(...siblingValueCells);
  }

  const titleRow = new TableRow({
    children: titleCells,
  });
  const valueRow = new TableRow({
    children: valueCells,
  });

  const fieldTable = new Table({
    width: {
      size: '80%',
      type: 'auto',
    },
    margins: {
      top: 50,
      bottom: 50,
    },
    rows: [titleRow, valueRow],
  });

  return fieldTable;
};

const getFontSizeFromSpanString = (htmlString: string) => {
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = htmlString;
  const spanElement = tempDiv.querySelector('span');
  const styleAttr = spanElement?.getAttribute('style');
  const styles = styleAttr?.split(';');
  if (!styles) {
    return {
      size: null,
    };
  }
  const keyVal = styles[0].split(':');
  const fontSizeObject = {
    size: keyVal[1].trim().replace('px', 'pt'),
  };

  return fontSizeObject;
};

const htmlToDocxObject = (content: string) => {
  const htmlTagMatch = content.match(/<\/?[^>]*(?:>|$)|[^<]+/g) || [];

  const paragraphStack = [];
  let currentSpanStyle: SpanStyle | null = null;
  const formattingStack: string[] = [];
  let paragraphContent: TextRun[] = [];
  let currentBullet: Bullet | null = null;
  let headingLevel = '';
  let isInList = false;
  let pCount = 0;

  htmlTagMatch.forEach((item, index) => {
    if (item.startsWith('<') && item.endsWith('>')) {
      // Handle HTML tags
      let tag = item.toLowerCase();

      if (pCount % 2 === 0 && tag === '<p>') {
        tag = '</p>';
      }

      //handle span, extract styles
      if (tag.includes('span')) {
        currentSpanStyle = getFontSizeFromSpanString(`${tag}</span>`);
      }
      switch (tag) {
        case '</span>':
          currentSpanStyle = null;
          break;
        case '<i>':
        case '<em>':
          formattingStack.push('italics');
          break;
        case '<b>':
        case '<strong>':
          formattingStack.push('bold');
          break;
        case '<u>':
          formattingStack.push('underline');
          break;
        case '</i>':
        case '</em>':
        case '</b>':
        case '</strong>':
        case '</u>':
          formattingStack.pop();
          break;
        case '<ul>':
          if (paragraphContent.length > 0) {
            paragraphStack.push(
              createParagraph(paragraphContent, currentBullet, headingLevel),
            );
          }
          currentBullet = { reference: 'bulleted-list', level: 0 };
          paragraphContent = [];
          break;
        case '<ol>':
          if (paragraphContent.length > 0) {
            paragraphStack.push(
              createParagraph(paragraphContent, currentBullet, headingLevel),
            );
          }
          currentBullet = { reference: 'numbered-list', level: 0 };
          paragraphContent = [];
          break;
        case '<li>':
          if (paragraphContent.length > 0) {
            paragraphStack.push(
              createParagraph(paragraphContent, currentBullet, headingLevel),
            );
          }
          paragraphContent = [];
          isInList = true;
          break;
        case '</ol>':
        case '</ul>':
          if (paragraphContent.length > 0) {
            paragraphStack.push(
              createParagraph(paragraphContent, currentBullet, headingLevel),
            );
          }
          currentBullet = null;
          paragraphContent = [];
          break;
        case '</li>':
          isInList = false;
          break;
        case '<h1>':
          if (paragraphContent.length > 0) {
            paragraphStack.push(
              createParagraph(paragraphContent, currentBullet, headingLevel),
            );
            paragraphContent = [];
          }
          headingLevel = HeadingLevel.HEADING_1;
          break;
        case '<h2>':
          if (paragraphContent.length > 0) {
            paragraphStack.push(
              createParagraph(paragraphContent, currentBullet, headingLevel),
            );
            paragraphContent = [];
          }
          headingLevel = HeadingLevel.HEADING_2;
          break;
        case '<h3>':
          if (paragraphContent.length > 0) {
            paragraphStack.push(
              createParagraph(paragraphContent, currentBullet, headingLevel),
            );
            paragraphContent = [];
          }
          headingLevel = HeadingLevel.HEADING_3;
          break;
        case '<h4>':
          if (paragraphContent.length > 0) {
            paragraphStack.push(
              createParagraph(paragraphContent, currentBullet, headingLevel),
            );
            paragraphContent = [];
          }
          headingLevel = HeadingLevel.HEADING_4;
          break;
        case '<h5>':
          if (paragraphContent.length > 0) {
            paragraphStack.push(
              createParagraph(paragraphContent, currentBullet, headingLevel),
            );
            paragraphContent = [];
          }
          headingLevel = HeadingLevel.HEADING_5;
          break;
        case '<h6>':
          if (paragraphContent.length > 0) {
            paragraphStack.push(
              createParagraph(paragraphContent, currentBullet, headingLevel),
            );
            paragraphContent = [];
          }
          headingLevel = HeadingLevel.HEADING_6;
          break;
        case '</h1>':
        case '</h2>':
        case '</h3>':
        case '</h4>':
        case '</h5>':
        case '</h6>':
          if (paragraphContent.length > 0) {
            paragraphStack.push(
              createParagraph(paragraphContent, currentBullet, headingLevel),
            );
          }
          headingLevel = '';
          paragraphContent = [];
          break;
        case '<br/>':
        case '<br>':
          paragraphContent.push(new TextRun({ break: 1 }));
          break;
        case '</p>':
        case '<p>':
          pCount++;
          if (
            htmlTagMatch[index + 1] == '<p>' &&
            htmlTagMatch[index] == '</p>'
          ) {
            break;
          }
          // Add a line break
          if (pCount == 1 && paragraphContent.length > 0) {
            paragraphContent.push(new TextRun({ break: 1 }));
          }
          if (pCount != 1 && !isInList) {
            paragraphContent.push(new TextRun({ break: 1 }));
          }

          break;
      }
    } else {
      // Handle plain text
      const textProps: LooseObject = {
        text: item,
        font: DEFAULT_FONT,
      };

      if (formattingStack.includes('italics')) {
        textProps.italics = true;
      }
      if (formattingStack.includes('bold')) {
        textProps.bold = true;
      }

      textProps.size = currentSpanStyle ? currentSpanStyle.size : '12pt';

      if (formattingStack.includes('underline')) {
        textProps.underline = {
          type: UnderlineType.SINGLE,
          color: fontColors.black,
        };
      }

      const textRun = new TextRun(textProps);
      paragraphContent.push(textRun);
    }
  });

  // Add the last paragraph to the paragraphStack
  if (paragraphContent.length > 0) {
    paragraphStack.push(
      createParagraph(paragraphContent, currentBullet, headingLevel),
    );
  }

  return paragraphStack;
};

export const generateReportDocx = async (data: ModifiedFinalReportDocument) => {
  const headerImageRequest = await fetch(ConfideHeader);
  const headerImage = await headerImageRequest.arrayBuffer();

  const header = new Header({
    children: [
      new Paragraph({
        children: [
          new ImageRun({
            data: headerImage,
            transformation: {
              width: 800,
              height: 96,
            },
            floating: {
              horizontalPosition: {
                offset: 0,
              },
              verticalPosition: {
                offset: 0,
              },
            },
          }),
        ],
      }),
    ],
  });

  const getLocalizedFooter = () => {
    const intl = getCurrentIntl();

    return new Footer({
      children: [
        new Table({
          width: {
            size: '100%',
            type: 'auto',
          },
          rows: [
            new TableRow({
              children: [
                new TableCell({
                  borders: footerBorder,
                  margins: {
                    top: 140,
                  },
                  children: [new Paragraph('')],
                }),
                new TableCell({
                  borders: footerBorder,
                  margins: {
                    top: 2,
                  },
                  children: [new Paragraph('')],
                }),
                new TableCell({
                  borders: footerBorder,
                  margins: {
                    top: 2,
                  },
                  children: [
                    new Paragraph({
                      alignment: AlignmentType.RIGHT,
                      children: [
                        new TextRun({
                          children: [
                            intl.formatMessage({ defaultMessage: 'Page' }),
                            ' ',
                            PageNumber.CURRENT,
                          ],
                          font: DEFAULT_FONT,
                        }),
                      ],
                    }),
                  ],
                }),
              ],
            }),
          ],
        }),
      ],
    });
  };

  //function for getting localized informed individuals
  //no need to assign to a variable since this is only used once
  const informedIndividualsSection = () => {
    const intl = getCurrentIntl();

    //informed individual column names
    const informedIndividualsColumns: string[] = [
      'Name',
      'Associated Department',
      'Notification Reason',
      'Date Informed',
    ];

    //translate informed individual column names to table cells
    const informedIndividualsColumnsDisplay: TableCell[] =
      informedIndividualsColumns.map((informedIndividualsColumn) => {
        return new TableCell({
          borders: bottomBorder,
          margins: {
            bottom: 100,
            right: 140,
          },
          children: [
            new Paragraph({
              alignment: AlignmentType.LEFT,
              children: [
                new TextRun({
                  children: [informedIndividualsColumn],
                  bold: true,
                  font: DEFAULT_FONT,
                  size: '12pt',
                }),
              ],
            }),
          ],
        });
      });

    //scoped helper function creating table cell for informed individual
    const createTableCell = (text: string) =>
      new TableCell({
        margins: { bottom: 25, right: 140, top: 100 },
        borders: noBorder,
        children: [
          new Paragraph({
            alignment: AlignmentType.LEFT,
            children: [
              new TextRun({
                children: [text],
                font: DEFAULT_FONT,
                size: '12pt',
              }),
            ],
          }),
        ],
      });

    //map informed individual items
    const informedIndividualsItems: TableRow[] = data.informed_individuals.map(
      (informedIndividual) => {
        return new TableRow({
          children: [
            createTableCell(informedIndividual.name),
            createTableCell(informedIndividual.associatedDepartment),
            createTableCell(informedIndividual.notificationReason),
            createTableCell(informedIndividual.dateInformed),
          ],
        });
      },
    );

    return new Table({
      rows: [
        new TableRow({
          children: [
            new TableCell({
              borders: noBorder,
              margins: {
                bottom: 275,
              },
              children: [
                new Paragraph({
                  children: [
                    new TextRun({
                      children: [
                        intl.formatMessage({
                          defaultMessage: 'Informed Individuals',
                        }),
                      ],
                      bold: true,
                      font: DEFAULT_FONT,
                      size: '16pt',
                    }),
                  ],
                }),
              ],
            }),
          ],
        }),
        new TableRow({
          children: informedIndividualsColumnsDisplay,
        }),
        ...informedIndividualsItems,
      ],
    });
  };

  const getLocalizedTitle = () => {
    const intl = getCurrentIntl();
    return new Table({
      width: {
        size: '100%',
        type: 'auto',
      },
      rows: [
        new TableRow({
          children: [
            new TableCell({
              borders: noBorder,
              margins: {
                bottom: 275,
              },
              children: [
                new Paragraph({
                  children: [
                    new TextRun({
                      children: [data.title],
                      bold: true,
                      font: DEFAULT_FONT,
                      size: '16pt',
                    }),
                  ],
                }),
              ],
            }),
            new TableCell({
              borders: noBorder,
              margins: {
                bottom: 20,
              },
              children: [new Paragraph('')],
            }),
            new TableCell({
              borders: noBorder,
              margins: {
                bottom: 20,
              },
              children: [
                new Paragraph({
                  alignment: AlignmentType.RIGHT,
                  children: [
                    new TextRun({
                      children: [
                        intl.formatMessage({ defaultMessage: 'Final Report' }),
                      ],
                      font: DEFAULT_FONT,
                      size: '14pt',
                      color: fontColors.darkGray,
                    }),
                  ],
                }),
              ],
            }),
          ],
        }),
      ],
    });
  };

  const getLocalizedDetailsAndDateClosed = () => {
    const intl = getCurrentIntl();
    return new Table({
      width: {
        size: '100%',
        type: 'auto',
      },
      rows: [
        new TableRow({
          children: [
            new TableCell({
              borders: bottomBorder,
              verticalAlign: 'bottom',
              margins: {
                bottom: 75,
              },
              children: [
                new Paragraph({
                  children: [
                    new TextRun({
                      children: [
                        intl.formatMessage({ defaultMessage: 'Details' }),
                      ],
                      bold: true,
                      font: DEFAULT_FONT,
                      size: '14pt',
                    }),
                  ],
                }),
              ],
            }),
            new TableCell({
              borders: bottomBorder,
              margins: {
                bottom: 20,
              },
              children: [new Paragraph('')],
            }),
          ],
        }),
      ],
    });
  };

  const getFixedDetailsData = () => {
    const intl = getCurrentIntl();
    const fixedDetails = [];
    if (data.assignee) {
      const caseManagerData = {
        field: intl.formatMessage({ defaultMessage: 'Case Manager' }),
        value: data.assignee,
      };
      fixedDetails.push(caseManagerData);
    }
    if (data.dateReceived) {
      const dateReceivedData = {
        field: intl.formatMessage({ defaultMessage: 'Date Received' }),
        value: data.dateReceived,
      };
      fixedDetails.push(dateReceivedData);
    }
    if (data.reportType) {
      const reportTypeData = {
        field: intl.formatMessage({ defaultMessage: 'Report Type' }),
        value: data.reportType,
      };
      fixedDetails.push(reportTypeData);
    }
    if (data.referenceID) {
      const reportReferenceData = {
        field: intl.formatMessage({
          defaultMessage: 'Report Reference',
        }),
        value: data.referenceID,
      };
      fixedDetails.push(reportReferenceData);
    }
    if (data.priority) {
      const reportSeverityData = {
        field: intl.formatMessage({
          defaultMessage: 'Report Severity',
        }),
        value: data.priority,
      };
      fixedDetails.push(reportSeverityData);
    }

    return fixedDetails;
  };

  const footer = getLocalizedFooter();

  const reportTitle = getLocalizedTitle();

  const reportDetailsAndDateClosed = getLocalizedDetailsAndDateClosed();

  const fixedDetailsData = getFixedDetailsData();

  const fixedDetailsRows = fixedDetailsData.map((detail) => {
    return new TableRow({
      children: [
        new TableCell({
          borders: noBorder,
          children: [
            new Paragraph({
              children: [
                new TextRun({
                  children: [`${detail.field} :`],
                  font: DEFAULT_FONT,
                  size: '12pt',
                  color: fontColors.darkGray,
                }),
              ],
            }),
          ],
        }),
        new TableCell({
          width: {
            type: 'auto',
            size: '65%',
          },
          borders: noBorder,
          children: [
            new Paragraph({
              children: [
                new TextRun({
                  children: [`${detail.value}`],
                  font: DEFAULT_FONT,
                  size: '12pt',
                  color: fontColors.black,
                  bold: true,
                  allCaps: true,
                }),
              ],
            }),
          ],
        }),
      ],
    });
  });

  const fixedDetails =
    fixedDetailsRows.length > 0
      ? new Table({
        width: {
          size: '80%',
        },
        margins: {
          top: 25,
          bottom: 25,
        },
        rows: fixedDetailsRows,
      })
      : new Paragraph('');

  const customDetailStack: DocxObject[] = [];

  data.fieldValues.forEach((field) => {
    switch (field.type) {
      case fieldTypes.title:
        customDetailStack.push(
          new Paragraph({
            children: [
              new TextRun({
                text: field.title,
                font: DEFAULT_FONT,
                bold: true,
                size: '16pt',
              }),
            ],
            spacing: {
              line: ONE_FIVE_LINE_SPACING,
              lineRule: 'auto',
            },
          }),
        );
        break;
      case fieldTypes.descriptionText:
        customDetailStack.push(...htmlToDocxObject(field.title));
        break;
      case fieldTypes.bigTextField:
        customDetailStack.push(
          new Paragraph({
            children: [
              new TextRun({
                text: field.title,
                font: DEFAULT_FONT,
                size: '12pt',
                bold: true,
              }),
            ],
            spacing: {
              line: ONE_FIVE_LINE_SPACING,
              lineRule: 'auto',
            },
          }),
        );
        customDetailStack.push(...htmlToDocxObject(field.value));
        break;
      default:
        customDetailStack.push(createSmallFieldTable(field));
        customDetailStack.push(new Paragraph(''));
        break;
    }
  });

  //default document's children
  const documentChildren = [
    reportTitle,
    reportDetailsAndDateClosed,
    new Paragraph(''),
    fixedDetails,
    new Paragraph(''),
    ...customDetailStack,
  ];

  //insert informed individuals section in the document children array if user has informed individuals
  if (data.has_informed_individuals && data.informed_individuals.length > 0) {
    documentChildren.splice(
      5,
      0,
      ...[informedIndividualsSection(), new Paragraph('')],
    );
  }

  const document = new Document({
    sections: [
      {
        properties: {
          page: {
            size: {
              width: '210mm',
              height: '297mm',
            },
            margin: {
              top: '35mm',
            },
          },
        },
        children: documentChildren,
        headers: {
          default: header,
        },
        footers: {
          default: footer,
        },
      },
    ],
    numbering: {
      config: [
        {
          reference: 'bulleted-list',
          levels: [
            {
              level: 0,
              format: LevelFormat.BULLET,
              text: '\u00B7',
              alignment: AlignmentType.LEFT,
              style: {
                paragraph: {
                  indent: {
                    left: convertInchesToTwip(0.5),
                    hanging: convertInchesToTwip(0.18),
                  },
                },
                run: {
                  font: DEFAULT_FONT,
                },
              },
            },
          ],
        },
        {
          reference: 'numbered-list',
          levels: [
            {
              level: 0,
              format: LevelFormat.DECIMAL,
              text: '%1.',
              alignment: AlignmentType.LEFT,
              style: {
                paragraph: {
                  indent: {
                    left: convertInchesToTwip(0.5),
                    hanging: convertInchesToTwip(0.18),
                  },
                },
                run: {
                  font: DEFAULT_FONT,
                },
              },
            },
          ],
        },
      ],
    },
  });

  Packer.toBlob(document).then((docBlob) => {
    saveAs(docBlob, 'FinalReport.docx');
  });
};
